"use client";
import { type Logger, createLogger } from "@offstreetparking/logger";
import { DATADOG_SITE } from "../lib/constants";
import { shouldFilterDatadogPayload } from "../lib/error-filters";
import { redactLogsEvent, redactRumEvent } from "../lib/redact-personal-data";
import type { CaptureContext, ClientTracingOptions, TracingEnvironment } from "../lib/types";
import { clientTracingOptionsSchema, tracingEnvironmentSchema } from "../lib/types";
import { normalizeError } from "./normalize-error";

type DatadogRumClient = typeof import("@datadog/browser-rum-slim").datadogRum;
type DatadogLogsClient = typeof import("@datadog/browser-logs").datadogLogs;

const defaultLogger = createLogger({ service: "instrumentation" });

let datadogInitialized = false;
let datadogPending = false;
let _datadogRum: DatadogRumClient | null = null;
let _datadogLogs: DatadogLogsClient | null = null;

// Pre-init queue — API calls that arrive before the deferred SDK import resolves
// are buffered and flushed automatically once init completes.
type QueuedCall =
	| { type: "addError"; args: [Error, CaptureContext | undefined] }
	| { type: "setUser"; args: [{ id: string } & Record<string, unknown>] }
	| { type: "clearUser" };

const preInitQueue: QueuedCall[] = [];

type DatadogInternalContext = {
	session_id?: unknown;
	view?: {
		id?: unknown;
	};
};

export function isTracingInitialized(): boolean {
	return datadogInitialized;
}

export function enableClientTracing({
	environment: env,
	logger = defaultLogger,
	...options
}: ClientTracingOptions & { logger?: Logger }): void {
	try {
		const environment = tracingEnvironmentSchema.parse(env);
		if (!environment || environment === "development" || environment === "automatedtest") {
			logger.info("[Instrumentation] Tracing disabled");
			return;
		}

		if (datadogInitialized || datadogPending) {
			return;
		}

		const validationResult = clientTracingOptionsSchema.safeParse(options);
		if (!validationResult.success) {
			logger.warn("[Instrumentation] Invalid client tracing configuration", {
				error: validationResult.error,
			});
			return;
		}

		if (!validationResult.data.datadogApplicationId || !validationResult.data.datadogClientToken) {
			return;
		}

		if (typeof window === "undefined") {
			return;
		}

		datadogPending = true;
		scheduleDatadogInit({ ...validationResult.data, environment }, logger);
	} catch (error: unknown) {
		logger.error("[Instrumentation] Failed to enable client tracing", normalizeError(error));
	}
}

function scheduleDatadogInit(
	config: Omit<ClientTracingOptions, "environment"> & { environment: TracingEnvironment },
	logger: Logger,
): void {
	const run = () => {
		void initDatadog(config, logger);
	};

	if ("requestIdleCallback" in window) {
		// Hard 2s cap so a backgrounded/throttled tab cannot leave init pending
		// indefinitely — otherwise the pre-init queue would grow unbounded.
		window.requestIdleCallback(run, { timeout: 2000 });
	} else {
		setTimeout(run, 0);
	}
}

async function initDatadog(
	{
		service,
		environment,
		version,
		datadogApplicationId,
		datadogClientToken,
		apiUrl,
		sampleRate,
		redactPersonalData = false,
	}: Omit<ClientTracingOptions, "environment"> & { environment: TracingEnvironment },
	logger: Logger,
): Promise<void> {
	try {
		const [{ datadogRum }, { datadogLogs }] = await Promise.all([
			import("@datadog/browser-rum-slim"),
			import("@datadog/browser-logs"),
		]);

		if (datadogInitialized) {
			return;
		}

		const isProd = environment === "production";
		const defaultSampleRate = isProd ? 0.1 : 1.0;
		const sampleRatePercent = sampleRate ?? defaultSampleRate;
		const sampleRateValue = Math.min(Math.max(sampleRatePercent * 100, 0), 100);

		datadogRum.init({
			applicationId: datadogApplicationId,
			clientToken: datadogClientToken,
			site: DATADOG_SITE,
			service,
			env: environment,
			version,
			sessionSampleRate: sampleRateValue,
			allowedTracingUrls: redactPersonalData ? [] : [{ match: apiUrl, propagatorTypes: ["tracecontext"] }],
			trackResources: true,
			trackLongTasks: true,
			trackUserInteractions: true,
			trackAnonymousUser: !redactPersonalData,
			enablePrivacyForActionName: redactPersonalData,
			beforeSend: (event) => {
				if (shouldFilterDatadogPayload(event)) {
					return false;
				}
				if (redactPersonalData) {
					redactRumEvent(event);
				}
				return true;
			},
		});

		datadogLogs.init({
			clientToken: datadogClientToken,
			site: DATADOG_SITE,
			forwardErrorsToLogs: true,
			sessionSampleRate: sampleRateValue,
			trackAnonymousUser: !redactPersonalData,
			beforeSend: (log) => {
				if (shouldFilterDatadogPayload(log)) {
					return false;
				}
				if (redactPersonalData) {
					redactLogsEvent(log);
				}
				return true;
			},
		});

		_datadogRum = datadogRum;
		_datadogLogs = datadogLogs;
		datadogInitialized = true;
		datadogPending = false;
		flushPreInitQueue();
		logger.info("[Instrumentation] Tracing enabled");
	} catch {
		datadogPending = false;
	}
}

function flushPreInitQueue(): void {
	while (preInitQueue.length > 0) {
		const call = preInitQueue.shift();
		if (!call) {
			continue;
		}
		try {
			if (call.type === "addError") {
				_datadogRum?.addError(...call.args);
				_datadogLogs?.logger.error(call.args[0].message, call.args[1], call.args[0]);
			} else if (call.type === "setUser") {
				_datadogRum?.setUser(call.args[0]);
			} else if (call.type === "clearUser") {
				_datadogRum?.clearUser();
			}
		} catch {
			// ignore
		}
	}
}

export function captureException(error: Error, context?: CaptureContext) {
	if (!datadogInitialized) {
		if (datadogPending) {
			preInitQueue.push({ type: "addError", args: [error, context] });
			return;
		}
		defaultLogger.error("[Instrumentation] Tracing not initialized", error);
		return;
	}
	try {
		_datadogRum?.addError(error, context);
		_datadogLogs?.logger.error(error.message, context, error);
		return getDatadogReferenceId();
	} catch (err: unknown) {
		defaultLogger.error("[Instrumentation] Failed to capture exception", normalizeError(err));
	}
}

export function getDatadogReferenceId(): string | undefined {
	if (!datadogInitialized || !_datadogRum || typeof window === "undefined") {
		return undefined;
	}

	try {
		const context = _datadogRum.getInternalContext() as DatadogInternalContext | undefined;
		const viewId = typeof context?.view?.id === "string" ? context.view.id : undefined;
		const sessionId = typeof context?.session_id === "string" ? context.session_id : undefined;

		if (viewId && sessionId) {
			return `${viewId}:${sessionId}`;
		}

		return viewId ?? sessionId;
	} catch {
		return undefined;
	}
}

export function captureMessage(message: string, context?: CaptureContext) {
	if (!datadogInitialized) {
		defaultLogger.warn("[Instrumentation] Tracing not initialized. Message not captured", { message });
		return;
	}
	try {
		_datadogLogs?.logger.info(message, context);
		return getDatadogReferenceId();
	} catch (error: unknown) {
		defaultLogger.error("[Instrumentation] Failed to capture message", normalizeError(error));
	}
}

export function setUser({ id, ...attributes }: { id: number } & Record<string, unknown>): void {
	const normalized = { id: String(id), ...attributes };
	if (!datadogInitialized) {
		if (datadogPending) {
			preInitQueue.push({ type: "setUser", args: [normalized] });
			return;
		}
		defaultLogger.warn("[Instrumentation] Tracing not initialized. User context not set.");
		return;
	}
	try {
		_datadogRum?.setUser(normalized);
	} catch (error: unknown) {
		defaultLogger.error("[Instrumentation] Failed to set user context", normalizeError(error));
	}
}

export function clearUser(): void {
	if (!datadogInitialized) {
		if (datadogPending) {
			preInitQueue.push({ type: "clearUser" });
			return;
		}
		defaultLogger.warn("[Instrumentation] Tracing not initialized. User context not cleared.");
		return;
	}
	try {
		_datadogRum?.clearUser();
	} catch (error: unknown) {
		defaultLogger.error("[Instrumentation] Failed to clear user context", normalizeError(error));
	}
}

export function getTracingContext() {
	if (!datadogInitialized || !_datadogRum || typeof window === "undefined") {
		return {};
	}
	return {
		..._datadogRum.getGlobalContext(),
		..._datadogRum.getInternalContext(),
	};
}
